import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowsClockwiseIcon,
  DownloadSimpleIcon,
  FileCsvIcon,
  GitDiffIcon,
  TrashIcon,
  UserPlusIcon,
} from '@phosphor-icons/react';
import type { GetPricelistResponse } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { PricelistSidebarActionButton } from './PricelistSidebarActionButton';
import { PricelistAssignDialog } from '../PricelistAssignDialog';
import { PricelistEventHistory } from './PricelistEventHistory';
import { PricelistVersionHistory } from './PricelistVersionHistory';
import { useDeletePricelist, usePricelistActions } from './usePricelistActions';

export function PricelistFileActions({
  pricelist,
  onDeleted,
  showAssign = true,
}: {
  pricelist: GetPricelistResponse;
  onDeleted: () => void;
  showAssign?: boolean;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const actions = usePricelistActions(pricelist);
  const deleteMutation = useDeletePricelist(onDeleted);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <div className="space-y-2">
      {showAssign ? (
        <PricelistSidebarActionButton
          icon={<UserPlusIcon className="h-4 w-4" />}
          onClick={() => setAssignOpen(true)}
        >
          {pricelist.companyName !== null ? (
            <T context="pricelist sidebar: reassign to another company">
              Reassign
            </T>
          ) : (
            <T context="pricelist sidebar: assign to a company">Assign</T>
          )}
        </PricelistSidebarActionButton>
      ) : null}

      <PricelistSidebarActionButton
        icon={<DownloadSimpleIcon className="h-4 w-4" />}
        busy={actions.loadingFile}
        onClick={() => void actions.downloadFile()}
      >
        <T context="pricelist sidebar: download the uploaded source file">
          Download file
        </T>
      </PricelistSidebarActionButton>

      <PricelistSidebarActionButton
        icon={<FileCsvIcon className="h-4 w-4" />}
        busy={actions.loadingCsv}
        disabled={actions.csvDoc === undefined}
        onClick={() => void actions.downloadCsv()}
      >
        {actions.csvDoc ? (
          <T context="pricelist sidebar: download the extracted CSV">
            Download CSV
          </T>
        ) : (
          <T context="pricelist sidebar: disabled CSV download when no extraction exists">
            No extraction found
          </T>
        )}
      </PricelistSidebarActionButton>

      <PricelistSidebarActionButton
        icon={<GitDiffIcon className="h-4 w-4" />}
        disabled={actions.csvDoc === undefined}
        onClick={() =>
          void navigate({
            to: '/item-master/utilities/compare-csvs',
            search: { mode: 'pricelists', left: pricelist.id },
          })
        }
      >
        <T context="pricelist sidebar: open the compare tool with this pricelist in the first slot">
          Compare with another pricelist
        </T>
      </PricelistSidebarActionButton>

      <PricelistSidebarActionButton
        icon={<ArrowsClockwiseIcon className="h-4 w-4" />}
        busy={actions.isReExtracting}
        onClick={() => void actions.reExtract()}
      >
        <T context="pricelist sidebar: re-run the CSV extraction">
          Re-extract CSV
        </T>
      </PricelistSidebarActionButton>

      <Button
        variant="outline"
        className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
        onClick={() => setDeleteOpen(true)}
      >
        <TrashIcon className="h-4 w-4" />
        <T context="pricelist sidebar: delete this pricelist">Delete</T>
      </Button>

      <PricelistVersionHistory documentId={pricelist.id} />

      <PricelistEventHistory documentId={pricelist.id} />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate({ pdfDocumentId: pricelist.id })}
        title={
          <T context="delete pricelist confirmation title">
            Delete this pricelist?
          </T>
        }
        message={gt(
          'It will be removed from the pricelist views along with its extracted data.',
          { context: 'delete pricelist confirmation body' },
        )}
        confirmText={gt('Delete', { context: 'delete confirm button' })}
        cancelText={gt('Cancel', { context: 'cancel button' })}
        loading={deleteMutation.isPending}
        dangerous
        closeOnConfirm={false}
      />

      {showAssign ? (
        <PricelistAssignDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          pdfDocumentId={pricelist.id}
          currentCompanyName={pricelist.companyName}
          onAssigned={actions.invalidatePricelistViews}
        />
      ) : null}
    </div>
  );
}
